import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from '@intrasync/stores'
// Using API types for ServiceStore entities
import type { Invoice } from 'types/common/ServiceStoreTypes'

export interface POFormData {
  invoiceId?: number
  supplierId?: number
  invoiceNumber?: string
  poNumber?: string
  orderDate?: Date
  expectedDeliveryDate?: Date
  priority?: 'Low' | 'Medium' | 'High' | 'Critical'
  notes?: string
  billingAddress?: string
  shippingAddress?: string
  totalAmount?: number
  currency?: string
  paymentTerms?: string
  deliveryTerms?: string
  specialInstructions?: string
}

export interface ConversionCheck {
  canConvert: boolean
  reason?: string
  invoice?: Invoice
}

export class InvoiceConversionViewStore {
  // Observables - UI state only
  currentStep = 0
  selectedInvoiceId: number | null = null
  poFormData: Partial<POFormData> = {}
  selectedApproverIds: string[] = []
  validationErrors: Map<string, string> = new Map()
  isDirty = false
  isSaving = false
  isSubmitting = false
  conversionCheckResult: ConversionCheck | null = null
  serviceHasError = false
  errorMessage: string | null = null
  isCheckingConversion = false
  selectedLineItemIds: string[] = []
  conversionSuccess = false
  includeAllLineItems = true

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false } as any)
  }

  // Actions
  setCurrentStep = (step: number) => {
    this.currentStep = step
  }

  selectInvoice = (invoiceId: number) => {
    this.selectedInvoiceId = invoiceId
    this.isDirty = true

    // Pre-populate PO data from invoice
    const invoice = (this.rootStore as any).invoicesStore.entities.find((inv: any) =>
      inv.id === invoiceId || inv.invoiceId === invoiceId
    )

    if (invoice) {
      runInAction(() => {
        // Determine supplier ID - if customerId represents a vendor/supplier
        const supplierId = invoice.customerId

        // Calculate total from line items if available
        let totalAmount = 0

        if (invoice.lineItems && invoice.lineItems.length > 0) {
          totalAmount = invoice.lineItems.reduce((sum: number, item: any) => {
            const quantity = item.quantity || 0
            const unitPrice = item.unitPrice || 0
            return sum + (quantity * unitPrice)
          }, 0)
        } else {
          totalAmount = Number(invoice.totalAmount) || 0
        }

        this.poFormData = {
          invoiceId: invoice.id || invoice.invoiceId,
          supplierId: supplierId,
          invoiceNumber: invoice.invoiceNumber,
          billingAddress: invoice.billingAddress || '',
          shippingAddress: invoice.shippingAddress || '',
          notes: invoice.notes || '',
          totalAmount: totalAmount,
          currency: invoice.currency || 'USD',
          paymentTerms: invoice.paymentTerms ? `${invoice.paymentTerms} days` : 'Net 30',
          priority: 'Medium',
          orderDate: new Date() // Initialize order date when selecting invoice
        }
      })
    }
  }

  updatePOFormData = (data: Partial<POFormData>) => {
    this.poFormData = { ...this.poFormData, ...data }
    this.isDirty = true
  }

  setSelectedApprovers = (approverIds: string[]) => {
    this.selectedApproverIds = approverIds
  }

  setSelectedLineItems = (lineItemIds: string[]) => {
    this.selectedLineItemIds = lineItemIds
    this.isDirty = true
  }

  setIncludeAllLineItems = (includeAll: boolean) => {
    this.includeAllLineItems = includeAll
    if (includeAll) {
      // Select all line items from invoice
      const invoice = this.selectedInvoice
      if (invoice?.lineItems) {
        this.selectedLineItemIds = invoice.lineItems.map((item: any) => 
          item.id || item.lineItemId
        )
      }
    }
  }

  addValidationError = (field: string, message: string) => {
    runInAction(() => {
      this.validationErrors.set(field, message)
    })
  }

  clearValidationErrors = () => {
    runInAction(() => {
      this.validationErrors.clear()
    })
  }

  // Async actions
  checkConversion = async () => {
    if (!this.selectedInvoiceId) return

    runInAction(() => {
      this.isCheckingConversion = true
      this.clearValidationErrors()
    })

    try {
      const foundInvoice = (this.rootStore as any).invoicesStore.entities.find(
        (inv: any) => inv.id === this.selectedInvoiceId || inv.invoiceId === this.selectedInvoiceId
      )

      // Check if invoice is already converted
      if (foundInvoice?.purchaseOrderId) {
        runInAction(() => {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'This invoice has already been linked to a purchase order',
            invoice: foundInvoice
          }
          this.isCheckingConversion = false
        })
        return
      }

      // Check invoice status
      if (foundInvoice?.status === 'cancelled') {
        runInAction(() => {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'Cannot convert cancelled invoices',
            invoice: foundInvoice
          }
          this.isCheckingConversion = false
        })
        return
      }

      // Check if invoice is from a vendor (not a customer invoice)
      // This would need backend support to distinguish vendor vs customer invoices
      const isVendorInvoice = await this.checkIfVendorInvoice(foundInvoice)

      runInAction(() => {
        if (!foundInvoice) {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'Invoice not found'
          }
        } else if (!isVendorInvoice) {
          this.conversionCheckResult = {
            canConvert: false,
            reason: 'Only vendor invoices can be converted to purchase orders',
            invoice: foundInvoice
          }
        } else {
          this.conversionCheckResult = {
            canConvert: true,
            invoice: foundInvoice
          }
        }
        this.isCheckingConversion = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.conversionCheckResult = {
          canConvert: false,
          reason: error.message || 'Failed to validate invoice'
        }
        this.addValidationError('conversion', error.message || 'Failed to validate invoice')
        this.isCheckingConversion = false
      })
    }
  }

  checkIfVendorInvoice = async (invoice: any): Promise<boolean> => {
    // Check if the invoice is from a vendor/supplier
    // This logic depends on how vendors are identified in the system
    
    // Option 1: Check if customerId is actually a supplier
    const suppliersStore = (this.rootStore as any).suppliersStore
    if (suppliersStore) {
      const isSupplier = suppliersStore.entities.find((supplier: any) => 
        supplier.id === invoice.customerId || supplier.supplierId === invoice.customerId
      )
      if (isSupplier) return true
    }

    // Option 2: Check invoice type field if it exists
    if (invoice.type === 'vendor' || invoice.invoiceType === 'vendor') {
      return true
    }

    // Option 3: Check if invoice has a specific field marking it as vendor invoice
    if (invoice.isVendorInvoice) {
      return true
    }

    // Default: For demo purposes, allow conversion if status is 'draft' or 'sent'
    return invoice.status === 'draft' || invoice.status === 'sent'
  }

  convertToPO = async () => {
    if (!this.validateConversion()) {
      this.addValidationError('form', 'Please complete all required fields before converting')
      return false
    }

    runInAction(() => {
      this.isSubmitting = true
      this.clearValidationErrors()
      this.conversionSuccess = false
    })

    try {
      // Create PO using the purchase-orders service
      const purchaseOrdersStore = (this.rootStore as any).purchaseOrdersStore

      if (!purchaseOrdersStore) {
        throw new Error('Purchase orders service not available')
      }

      // Prepare PO data from invoice and form data
      const invoice = this.selectedInvoice
      const currentUser = (this.rootStore as any).authStore?.user

      // Ensure we have a supplier ID
      const supplierId = this.poFormData.supplierId || invoice?.customerId
      if (!supplierId) {
        throw new Error('Supplier ID is required to create a purchase order')
      }

      // Format dates as required by backend
      const formatDate = (date: Date | undefined): string => {
        const d = date || new Date()
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      const orderDate = formatDate(this.poFormData.orderDate)
      const expectedDeliveryDate = this.poFormData.expectedDeliveryDate 
        ? formatDate(this.poFormData.expectedDeliveryDate)
        : undefined

      // Create PO data matching backend requirements
      const poData = {
        // Required fields
        poNumber: this.poFormData.poNumber!,
        accountId: Number(supplierId), // Backend uses accountId for supplier
        requestedBy: currentUser?.id || currentUser?.userId || 1, // Current user
        orderDate: orderDate,
        totalAmount: Number(this.poFormData.totalAmount) || Number(invoice?.totalAmount) || 0,
        currency: this.poFormData.currency || invoice?.currency || 'USD',
        
        // Optional fields
        status: 'Draft' as const,
        priority: this.poFormData.priority || 'Medium',
        expectedDeliveryDate: expectedDeliveryDate,
        paymentTerms: this.poFormData.paymentTerms,
        deliveryTerms: this.poFormData.deliveryTerms,
        shippingAddress: this.poFormData.shippingAddress || invoice?.shippingAddress,
        billingAddress: this.poFormData.billingAddress || invoice?.billingAddress,
        notes: this.poFormData.notes || invoice?.notes,
        specialInstructions: this.poFormData.specialInstructions,
        
        // Link to invoice
        invoiceId: this.selectedInvoiceId,
        invoiceNumber: invoice?.invoiceNumber,
        
        // Job/opportunity if applicable
        jobId: invoice?.jobId,
        opportunityId: invoice?.opportunityId
      }

      // Create the purchase order
      const purchaseOrder = await purchaseOrdersStore.create(poData)

      // Create line items if invoice has them and backend supports it
      if (this.selectedLineItemIds.length > 0 && (this.rootStore as any).purchaseOrderLineItemsStore) {
        const lineItemsStore = (this.rootStore as any).purchaseOrderLineItemsStore
        
        // Get invoice line items (would need backend support)
        // For now, we'll create basic line items
        for (const lineItemId of this.selectedLineItemIds) {
          try {
            await lineItemsStore.create({
              purchaseOrderId: purchaseOrder.purchaseOrderId,
              description: `Line item from invoice ${invoice?.invoiceNumber}`,
              quantity: 1,
              unitPrice: invoice?.totalAmount / this.selectedLineItemIds.length,
              totalPrice: invoice?.totalAmount / this.selectedLineItemIds.length
            })
          } catch (error) {
            // Continue with conversion even if line items fail
          }
        }
      }

      runInAction(() => {
        this.isSubmitting = false
        this.isDirty = false
        this.conversionSuccess = true

        // Update invoice to mark it as converted
        if ((this.rootStore as any).invoicesStore) {
          (this.rootStore as any).invoicesStore.patch(this.selectedInvoiceId!, {
            purchaseOrderId: purchaseOrder.purchaseOrderId,
            poNumber: purchaseOrder.poNumber
          }).catch(() => {
            // Don't fail the whole operation if invoice update fails
          })
        }
      })

      // Navigate to the created PO after a short delay
      const poIdToNavigate = purchaseOrder.purchaseOrderId || purchaseOrder.id
      
      setTimeout(() => {
        this.rootStore.routerStore.goTo('PurchaseOrderDetail', {
          purchaseOrderId: poIdToNavigate
        })
      }, 1500)

      return true
    } catch (error: any) {
      runInAction(() => {
        this.isSubmitting = false
        this.handleConversionError(error)
      })
      return false
    }
  }

  handleConversionError = (error: any) => {
    const errorMessage = error.message || error.data?.message || 'Unknown error'
    const errorCode = error.code || error.status

    if (errorCode === 409) {
      this.validationErrors.set('conversion', 'This invoice has already been converted to a purchase order')
      this.serviceHasError = true
      this.errorMessage = 'This invoice has already been converted'
    } else if (errorCode === 400) {
      if (error.data?.errors) {
        Object.entries(error.data.errors).forEach(([field, message]) => {
          this.validationErrors.set(field, String(message))
        })
      } else {
        this.validationErrors.set('conversion', errorMessage)
      }
      this.serviceHasError = true
      this.errorMessage = 'Please check the form for errors'
    } else if (errorCode === 404) {
      this.validationErrors.set('conversion', 'Invoice not found')
      this.serviceHasError = true
      this.errorMessage = 'The selected invoice could not be found'
    } else {
      this.validationErrors.set('conversion', errorMessage)
      this.serviceHasError = true
      this.errorMessage = errorMessage
    }
  }

  saveDraft = async () => {
    this.isSaving = true

    try {
      const draftData = {
        invoiceId: this.selectedInvoiceId,
        poFormData: this.poFormData,
        approverIds: this.selectedApproverIds,
        currentStep: this.currentStep,
        conversionType: 'invoice-to-po'
      }

      // Save draft if service exists
      if ((this.rootStore as any).conversionDraftsStore) {
        await (this.rootStore as any).conversionDraftsStore.create(draftData)
      }

      runInAction(() => {
        this.isSaving = false
        this.isDirty = false
      })
    } catch (error) {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  // Computed values - Reference ServiceStore entities
  get selectedInvoice() {
    if (!this.selectedInvoiceId) return null
    return (this.rootStore as any).invoicesStore.entities.find((inv: any) =>
      inv.id === this.selectedInvoiceId || inv.invoiceId === this.selectedInvoiceId
    )
  }

  get calculatedInvoiceTotal() {
    const invoice = this.selectedInvoice
    if (!invoice) return 0

    // If invoice has line items, calculate total from them
    if (invoice.lineItems && invoice.lineItems.length > 0) {
      return invoice.lineItems.reduce((sum: number, item: any) => {
        const quantity = item.quantity || 0
        const unitPrice = item.unitPrice || 0
        return sum + (quantity * unitPrice)
      }, 0)
    }

    // Otherwise use the invoice total
    return invoice.totalAmount || 0
  }

  get convertibleInvoices() {
    const invoices = (this.rootStore as any).invoicesStore.entities || []
    
    // Filter invoices that can be converted to POs
    return invoices.filter((inv: any) => {
      // Not already linked to a PO
      const notConverted = !inv.purchaseOrderId
      // Not cancelled
      const notCancelled = inv.status !== 'cancelled'
      // Is a vendor invoice (simplified check)
      const isVendor = inv.status === 'draft' || inv.status === 'sent' || inv.isVendorInvoice
      
      return notConverted && notCancelled && isVendor
    })
  }

  get availableApprovers() {
    return (this.rootStore as any).userStore?.entities
      .filter((user: any) => user.permissions?.includes('approve_purchase_orders')) || []
  }

  get requiresApproval() {
    const amount = this.poFormData.totalAmount || 0
    const priority = this.poFormData.priority
    // Business rule: High/Critical priority or amount > $5000 requires approval
    return priority === 'High' || priority === 'Critical' || amount > 5000
  }

  get canProceedToNext() {
    switch (this.currentStep) {
      case 0: return !!this.selectedInvoiceId && this.conversionCheckResult?.canConvert
      case 1: return this.isPODetailsValid
      case 2: return true // Line items optional
      case 3: return !this.requiresApproval || this.selectedApproverIds.length > 0
      case 4: return this.isConversionValid
      default: return false
    }
  }

  get isPODetailsValid() {
    const hasPoNumber = !!this.poFormData.poNumber && this.poFormData.poNumber.trim() !== ''
    const hasOrderDate = !!this.poFormData.orderDate
    const hasValidAmount = typeof this.poFormData.totalAmount === 'number' && 
                          this.poFormData.totalAmount > 0 && 
                          !isNaN(this.poFormData.totalAmount)
    
    return hasPoNumber && hasOrderDate && hasValidAmount
  }

  get isConversionValid() {
    return !!this.selectedInvoiceId &&
      this.isPODetailsValid &&
      (!this.requiresApproval || this.selectedApproverIds.length > 0)
  }

  // Validation methods
  validatePODetails = () => {
    runInAction(() => {
      this.clearValidationErrors()

      if (!this.poFormData.poNumber) {
        this.validationErrors.set('poNumber', 'PO number is required')
      }

      if (!this.poFormData.orderDate) {
        this.validationErrors.set('orderDate', 'Order date is required')
      }

      if (!this.poFormData.totalAmount || this.poFormData.totalAmount <= 0) {
        this.validationErrors.set('totalAmount', 'Total amount must be greater than 0')
      }
    })

    return this.validationErrors.size === 0
  }

  validateConversion = () => {
    return this.selectedInvoiceId &&
      this.isPODetailsValid &&
      (!this.requiresApproval || this.selectedApproverIds.length > 0)
  }

  // Cleanup
  clearErrors = () => {
    this.serviceHasError = false
    this.errorMessage = null
    this.validationErrors.clear()
  }

  reset = () => {
    this.currentStep = 0
    this.selectedInvoiceId = null
    this.poFormData = {}
    this.selectedApproverIds = []
    this.selectedLineItemIds = []
    this.validationErrors.clear()
    this.isDirty = false
    this.conversionCheckResult = null
    this.serviceHasError = false
    this.errorMessage = null
    this.isCheckingConversion = false
    this.conversionSuccess = false
    this.includeAllLineItems = true
  }
}